import { Content } from '@raydiant/api-client-js';
import cn from 'classnames';
import { useSelector } from 'react-redux';
import {
  getPresentationData,
  DEFAULT_SHARED_LIBRARY_FOLDER_DATA,
} from '../LibraryPage/utils';
import SidebarMenuTooltip from '../../components/Sidebar/SidebarMenuTooltip';
import { capitalize } from '../../utilities/stringUtils';
import { User } from '@raydiant/raydial/components/Icons';
import { Check } from './AddContentActionBarV2';
import ApplicationIcon from '../LibraryPage/v2/components/ApplicationIcon';
import Text from '@raydiant/raydial/components/Text';
import Spinner from '@raydiant/raydial/components/Spinner';
import UploadErrorIcon from '../../components/UploadErrorIcon';
import { selectLocalUploads } from '../../selectors/fileUploads';
import { getContentFileStatus } from '../../utilities/presentationUtils';

interface AddContentPageContentCardProps {
  content: Content;
  showOwner?: boolean;
  selected?: boolean;
  onClick: (content: Content) => void;
  showButtonGroup?: boolean;
  batchMode?: boolean;
  isTopLevelSharedLibraryFolder?: boolean;
}

const AddContentPageContentCard = ({
  content,
  showOwner = false,
  selected = false,
  onClick,
  batchMode = false,
  isTopLevelSharedLibraryFolder = false,
}: AddContentPageContentCardProps) => {
  const localUploads = useSelector(selectLocalUploads);
  const { isUploading: isFileUploading, hasError: hasUploadError } =
    getContentFileStatus(content, localUploads);

  const nextContent = {
    ...content,
    presentationData:
      isTopLevelSharedLibraryFolder && content.resourceType === 'folder'
        ? DEFAULT_SHARED_LIBRARY_FOLDER_DATA
        : getPresentationData(content),
  };

  const renderUploadStatusBadge = () => {
    if (isFileUploading) {
      return (
        <div
          className="absolute bottom-3 right-4 z-[2] flex items-center gap-1 rounded-md px-1.5 py-1 shadow-sm bg-gray-100"
          onClick={(e) => e.stopPropagation()}
        >
          <Spinner className="size-3 text-blue-600" />
          <span className="text-sm font-medium text-black">Uploading...</span>
        </div>
      );
    }
    if (hasUploadError) {
      return (
        <div
          className="absolute bottom-3 right-4 z-[2] flex items-center gap-1 rounded-md px-1.5 py-1 shadow-sm bg-red-50"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex size-4 shrink-0 items-center justify-center rounded-md">
            <UploadErrorIcon className="size-4 text-red-700" />
          </div>
          <span className="text-sm font-medium text-red-700">
            Upload Failed
          </span>
        </div>
      );
    }
    return null;
  };

  const renderShowOwnerInfo = () => {
    return (
      <SidebarMenuTooltip
        placement="bottom"
        title={`Owner: ${capitalize(content.ownedBy.name)}`}
        arrow
      >
        <div
          className="bg-white w-6 h-6 rounded-xl flex items-center justify-center text-gray-600"
          onClick={(e) => e.stopPropagation()}
        >
          <User size={15} className="mb-0.5" />
        </div>
      </SidebarMenuTooltip>
    );
  };

  const renderSelectStatus = (content: Content) => {
    return (
      <div
        className="bg-white w-6 h-6 rounded-xl flex items-center justify-center"
        onClick={(e) => {
          e.stopPropagation();
          onClick(content);
        }}
      >
        {selected && <Check className="fill-blue-600" />}
      </div>
    );
  };

  return (
    <div
      className={cn(
        'h-[185px] rounded-[20px] shadow border-2 border-grey-100 flex-col justify-start items-start inline-flex cursor-pointer',
        selected && 'border-blue-600',
      )}
      onClick={() => onClick(content)}
    >
      <div
        className="relative w-[216px] h-[125px] p-4 rounded-t-[17px] justify-between flex items-start bg-center bg-no-repeat bg-cover"
        style={{
          backgroundImage: `url(${
            nextContent.presentationData.thumbnailUrl ||
            content.presentationData.applicationThumbnailUrl
          })`,
        }}
      >
        {hasUploadError && (
          <div
            className="absolute inset-0 z-[1] rounded-t-[17px] bg-black/50"
            aria-hidden="true"
          />
        )}
        <div>{batchMode && renderSelectStatus(content)}</div>
        <div>{showOwner && renderShowOwnerInfo()}</div>
        {renderUploadStatusBadge()}
      </div>
      <div className="w-[216px] h-[60px] px-4 bg-white rounded-b-[20px] flex-col justify-center items-start gap-2.5 flex">
        <div className="self-stretch justify-start items-center gap-2 inline-flex">
          {hasUploadError ? (
            <div className="flex size-6 shrink-0 items-center justify-center rounded-md bg-red-50">
              <UploadErrorIcon className="size-5 text-red-700" />
            </div>
          ) : (
            <ApplicationIcon
              url={nextContent.presentationData.applicationIconUrl}
            />
          )}
          <div
            className={cn(
              'grow shrink basis-0 break-all line-clamp-2',
              hasUploadError ? 'text-gray-300' : 'text-gray-600',
            )}
          >
            <Text variant="body">{nextContent.name}</Text>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddContentPageContentCard;
